import { useCallback, useEffect, useState } from "react";
import api, { formatINR } from "../lib/api";
import { Wallet2, Plus, Users, Building2, Check, RotateCcw, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Payroll() {
  const [tab, setTab] = useState("salaries");
  const [month, setMonth] = useState(thisMonth());
  const [employees, setEmployees] = useState([]);
  const [salary, setSalary] = useState({ rows: [], total_paid: 0, total_pending: 0 });
  const [templates, setTemplates] = useState([]);
  const [office, setOffice] = useState({ rows: [], total_paid: 0, total_pending: 0 });
  const [payModal, setPayModal] = useState(null);
  const [empModal, setEmpModal] = useState(null);
  const [tplModal, setTplModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, s, t, o] = await Promise.all([
        api.get("/employees"), api.get(`/payroll?month=${month}`),
        api.get("/office-templates"), api.get(`/office-expenses?month=${month}`),
      ]);
      setEmployees(e.data || []); setSalary(s.data); setTemplates(t.data || []); setOffice(o.data);
    } catch { toast.error("Could not load payroll"); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  async function toggleSalary(r) {
    try {
      await api.post(`/payroll/${r.paid ? "unpay" : "pay"}`, { employee_id: r.employee_id, month });
      toast.success(r.paid ? "Marked unpaid" : `Paid ${r.name}`);
      load();
    } catch { toast.error("Action failed"); }
  }
  async function toggleOffice(r) {
    if (!r.paid) {
      setPayModal({ template_id: r.template_id, name: r.name, amount: r.amount,
        expense_date: new Date().toISOString().slice(0, 10), payment_method: "cash" });
      return;
    }
    try {
      await api.post(`/office-expenses/unpay`, { template_id: r.template_id, month });
      toast.success("Marked unpaid");
      load();
    } catch { toast.error("Action failed"); }
  }
  async function confirmPay() {
    try {
      await api.post(`/office-expenses/pay`, { template_id: payModal.template_id, month,
        amount: Number(payModal.amount) || 0, expense_date: payModal.expense_date,
        payment_method: payModal.payment_method });
      toast.success(`Paid ${payModal.name}`);
      setPayModal(null);
      load();
    } catch { toast.error("Action failed"); }
  }
  async function delEmployee(id) {
    if (!window.confirm("Delete this employee?")) return;
    try { await api.delete(`/employees/${id}`); load(); } catch { toast.error("Delete failed"); }
  }
  async function delTemplate(id) {
    if (!window.confirm("Delete this office expense?")) return;
    try { await api.delete(`/office-templates/${id}`); load(); } catch { toast.error("Delete failed"); }
  }

  return (
    <div data-testid="payroll-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Finance</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1 flex items-center gap-2"><Wallet2 className="w-8 h-8 text-[#C84B31]" /> Payroll & Office</h1>
          <p className="text-sm text-[#68665E] mt-1">Staff salaries and recurring office expenses. Marking Paid records it in Expenses & Reports.</p>
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="input-field !py-2 text-sm w-40" data-testid="payroll-month" />
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("salaries")} className={`px-4 py-2 rounded text-sm font-medium ${tab === "salaries" ? "bg-[#C84B31] text-white" : "bg-[#E5E3DB] text-[#4A4842]"}`} data-testid="tab-salaries"><Users className="w-4 h-4 inline mr-1" /> Salaries</button>
        <button onClick={() => setTab("office")} className={`px-4 py-2 rounded text-sm font-medium ${tab === "office" ? "bg-[#C84B31] text-white" : "bg-[#E5E3DB] text-[#4A4842]"}`} data-testid="tab-office"><Building2 className="w-4 h-4 inline mr-1" /> Office Expenses</button>
      </div>

      {tab === "salaries" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-4 text-sm">
              <span className="text-[#2F6F4E]">Paid: <b data-testid="salary-total-paid">{formatINR(salary.total_paid)}</b></span>
              <span className="text-[#8A2526]">Pending: <b data-testid="salary-total-pending">{formatINR(salary.total_pending)}</b></span>
            </div>
            <button onClick={() => setEmpModal({})} className="btn-primary text-sm" data-testid="add-employee-btn"><Plus className="w-4 h-4" /> Add Employee</button>
          </div>
          <div className="bg-white border border-[#E5E3DB] rounded-lg overflow-hidden">
            {salary.rows.length === 0 ? <div className="p-6 text-sm text-[#68665E] text-center">No active employees. Add one to start.</div> :
              salary.rows.map((r) => {
                const emp = employees.find((e) => e.id === r.employee_id);
                return (
                  <div key={r.employee_id} className="flex items-center gap-3 px-4 py-3 border-b border-[#F0EEE8] last:border-0" data-testid={`salary-row-${r.employee_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1A1A18]">{r.name} {r.role && <span className="text-xs text-[#68665E]">· {r.role}</span>}</div>
                      <div className="text-xs text-[#68665E]">{formatINR(r.amount)}{r.paid && r.paid_at ? ` · paid ${r.paid_at.slice(0, 10)}` : ""}</div>
                    </div>
                    <button onClick={() => toggleSalary(r)} data-testid={`salary-toggle-${r.employee_id}`}
                      className={`text-xs px-3 py-1.5 rounded font-medium ${r.paid ? "bg-[#E3F0E7] text-[#2F6F4E]" : "bg-[#C84B31] text-white"}`}>
                      {r.paid ? <><RotateCcw className="w-3.5 h-3.5 inline mr-1" />Paid</> : <><Check className="w-3.5 h-3.5 inline mr-1" />Mark Paid</>}
                    </button>
                    {emp && <button onClick={() => setEmpModal(emp)} className="btn-ghost !p-1.5" data-testid={`emp-edit-${r.employee_id}`}><Pencil className="w-4 h-4" /></button>}
                    <button onClick={() => delEmployee(r.employee_id)} className="btn-ghost !p-1.5 text-[#8A2526]" data-testid={`emp-delete-${r.employee_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {tab === "office" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-4 text-sm">
              <span className="text-[#2F6F4E]">Paid: <b data-testid="office-total-paid">{formatINR(office.total_paid)}</b></span>
              <span className="text-[#8A2526]">Pending: <b data-testid="office-total-pending">{formatINR(office.total_pending)}</b></span>
            </div>
            <button onClick={() => setTplModal({})} className="btn-primary text-sm" data-testid="add-office-btn"><Plus className="w-4 h-4" /> Add Office Expense</button>
          </div>
          <div className="bg-white border border-[#E5E3DB] rounded-lg overflow-hidden">
            {office.rows.length === 0 ? <div className="p-6 text-sm text-[#68665E] text-center">No office expenses yet. Add rent, electricity, internet…</div> :
              office.rows.map((r) => {
                const tpl = templates.find((t) => t.id === r.template_id);
                return (
                  <div key={r.template_id} className="flex items-center gap-3 px-4 py-3 border-b border-[#F0EEE8] last:border-0" data-testid={`office-row-${r.template_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1A1A18]">{r.name}</div>
                      <div className="text-xs text-[#68665E]">{formatINR(r.amount)}{r.paid && r.expense_date ? ` · ${r.expense_date}` : (r.paid && r.paid_at ? ` · paid ${r.paid_at.slice(0, 10)}` : "")}{r.paid && r.payment_method ? ` · ${r.payment_method}` : ""}</div>
                    </div>
                    <button onClick={() => toggleOffice(r)} data-testid={`office-toggle-${r.template_id}`}
                      className={`text-xs px-3 py-1.5 rounded font-medium ${r.paid ? "bg-[#E3F0E7] text-[#2F6F4E]" : "bg-[#C84B31] text-white"}`}>
                      {r.paid ? <><RotateCcw className="w-3.5 h-3.5 inline mr-1" />Paid</> : <><Check className="w-3.5 h-3.5 inline mr-1" />Mark Paid</>}
                    </button>
                    {tpl && <button onClick={() => setTplModal(tpl)} className="btn-ghost !p-1.5" data-testid={`tpl-edit-${r.template_id}`}><Pencil className="w-4 h-4" /></button>}
                    <button onClick={() => delTemplate(r.template_id)} className="btn-ghost !p-1.5 text-[#8A2526]" data-testid={`tpl-delete-${r.template_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {empModal && <EmployeeModal emp={empModal} onClose={() => setEmpModal(null)} onSaved={() => { setEmpModal(null); load(); }} />}
      {tplModal && <TemplateModal tpl={tplModal} onClose={() => setTplModal(null)} onSaved={() => { setTplModal(null); load(); }} />}
      {payModal && (
        <Modal title={`Mark paid — ${payModal.name}`} onClose={() => setPayModal(null)} testId="office-pay-modal">
          <div className="space-y-3">
            <label className="block"><span className="text-xs text-[#68665E]">Amount</span>
              <input type="number" className="input-field mt-1" value={payModal.amount} onChange={(e) => setPayModal((s) => ({ ...s, amount: e.target.value }))} data-testid="office-pay-amount" /></label>
            <label className="block"><span className="text-xs text-[#68665E]">Date paid</span>
              <input type="date" className="input-field mt-1" value={payModal.expense_date} onChange={(e) => setPayModal((s) => ({ ...s, expense_date: e.target.value }))} data-testid="office-pay-date" /></label>
            <label className="block"><span className="text-xs text-[#68665E]">Paid via</span>
              <select className="input-field mt-1" value={payModal.payment_method} onChange={(e) => setPayModal((s) => ({ ...s, payment_method: e.target.value }))} data-testid="office-pay-method">
                <option value="cash">Cash</option>
                <option value="bank">Bank transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </select></label>
            <button onClick={confirmPay} className="btn-primary w-full" data-testid="office-pay-confirm"><Check className="w-4 h-4" /> Confirm payment</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-headings font-bold text-[#1A1A18]">{title}</h3><button onClick={onClose}><X className="w-5 h-5 text-[#68665E]" /></button></div>
        {children}
      </div>
    </div>
  );
}

function EmployeeModal({ emp, onClose, onSaved }) {
  const [f, setF] = useState({ name: emp.name || "", role: emp.role || "", monthly_salary: emp.monthly_salary || 0, phone: emp.phone || "", join_date: emp.join_date || "", active: emp.active !== false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  async function save() {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    const body = { ...f, monthly_salary: Number(f.monthly_salary) || 0 };
    try {
      if (emp.id) await api.patch(`/employees/${emp.id}`, body);
      else await api.post("/employees", body);
      toast.success("Saved"); onSaved();
    } catch { toast.error("Save failed"); }
  }
  return (
    <Modal title={emp.id ? "Edit Employee" : "Add Employee"} onClose={onClose} testId="employee-modal">
      <div className="space-y-3">
        <input className="input-field !py-2 text-sm" placeholder="Full name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="emp-name" />
        <input className="input-field !py-2 text-sm" placeholder="Role (e.g. Accountant)" value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="emp-role" />
        <input type="number" className="input-field !py-2 text-sm" placeholder="Monthly salary" value={f.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} data-testid="emp-salary" />
        <input className="input-field !py-2 text-sm" placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        <label className="text-xs text-[#68665E]">Join date<input type="date" className="input-field !py-2 text-sm mt-1" value={f.join_date} onChange={(e) => set("join_date", e.target.value)} /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        <button onClick={save} className="btn-primary w-full" data-testid="emp-save">Save</button>
      </div>
    </Modal>
  );
}

function TemplateModal({ tpl, onClose, onSaved }) {
  const [f, setF] = useState({ name: tpl.name || "", default_amount: tpl.default_amount || 0, active: tpl.active !== false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  async function save() {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    const body = { ...f, default_amount: Number(f.default_amount) || 0 };
    try {
      if (tpl.id) await api.patch(`/office-templates/${tpl.id}`, body);
      else await api.post("/office-templates", body);
      toast.success("Saved"); onSaved();
    } catch { toast.error("Save failed"); }
  }
  return (
    <Modal title={tpl.id ? "Edit Office Expense" : "Add Office Expense"} onClose={onClose} testId="template-modal">
      <div className="space-y-3">
        <input className="input-field !py-2 text-sm" placeholder="Name (Rent, Electricity, Internet…)" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tpl-name" />
        <input type="number" className="input-field !py-2 text-sm" placeholder="Default monthly amount" value={f.default_amount} onChange={(e) => set("default_amount", e.target.value)} data-testid="tpl-amount" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
        <button onClick={save} className="btn-primary w-full" data-testid="tpl-save">Save</button>
      </div>
    </Modal>
  );
}
